import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppService } from '@app/app.service';
import { CustomValidators } from '@app/app.validator';
import { AppHelper } from '@app/core/app.helper';
import {
    DEFAULT_CROPPER_DIALOG_DATA,
    DEFAULT_CROP_OPTIONS,
    ICropperDialogData,
} from '@app/dialogs-helper/cropper-dialog/cropper-dialog.interface';
import { CatalogDialogComponent } from '@app/modules/catalogs/dialogs/catalog-dialog/catalog-dialog.component';
import { IImagePickerActions, IImagePickerData } from '@components/image-picker/image-picker.interface';
import { LANG_DICTIONARY_TOKEN, SERVER_URL_TOKEN } from '@constants/diConstant';
import { FuiSelectItemService } from '@fui/fui-components';
import { defaultStringNameItemService, IStringNameItem } from '@interfaces/fui-components/select.interface';
import { InterfaceAccessMatrixUnit } from '@modules/login/interfaces/login.interface';
import { prepareRubricData } from '@modules/rubricator/helpers/rubricator.helpers';
import { IUserListDialogData } from '@modules/users/interfaces/user-list.interface';
import { FuiDestroyService } from '@services/destroy.service';
import { FormToolsService } from '@services/form-tools.service';
import { FuiSelectService } from '@services/fui-components/select.service';
import { NotifierService } from '@services/notifier.service';
import { ValidationErrorService } from '@services/validation/validation-error.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { UserListService } from '../../../services/user-list.service';

@Component({
    selector: 'app-user-list-dialog',
    templateUrl: './user-list-dialog.component.html',
    styleUrls: ['../styles/user-list-dialog.component.css'],
    providers: [FuiDestroyService, ValidationErrorService, FormToolsService],
})
export class UserListDialogComponent implements OnInit {
    readonly #destroy$ = inject(FuiDestroyService);
    private readonly dialog = inject(MatDialog);
    private readonly spinner = inject(NgxSpinnerService);
    private readonly notifier = inject(NotifierService);
    private readonly dialogRef = inject(MatDialogRef<UserListDialogComponent>);
    private readonly formBuilder = inject(FormBuilder);
    private readonly service = inject(UserListService);
    private readonly formTools = inject(FormToolsService);
    readonly data: IUserListDialogData = inject(MAT_DIALOG_DATA);
    readonly appService = inject(AppService);
    readonly dictionary = inject(LANG_DICTIONARY_TOKEN);
    readonly serverUrl = inject(SERVER_URL_TOKEN);
    readonly fuiSelectService = inject(FuiSelectService);
    readonly validationErrorService = inject(ValidationErrorService);

    readonly now = new Date();
    readonly defaultStringNameItemService = defaultStringNameItemService;
    photoIds = [];

    /**
     * Дизаблить поля формы, доступные в режиме "Редактировать только себя"
     */
    disableProfileFields$ = new BehaviorSubject<boolean>(true);

    /**
     * Дизаблить поля формы, доступные в режиме "Редактировать других пользователей"
     */
    disableNonProfileFields$ = new BehaviorSubject<boolean>(true);

    /**
     * Загрузчик списка пунктов меню
     */
    readonly menuListLoader = this.fuiSelectService.getLoader<IStringNameItem>(
        this.appService.getRegisters([`menu_for_user(${this.data.id})`]).pipe(
            map((result) => result.data[`menu_for_user(${this.data.id})`] as IStringNameItem[]),
            map((items) => items.filter((x) => !!x.name)),
        ),
    );

    /**
     * Загрузчик списка подчинённых
     */
    readonly subordinates$ = new BehaviorSubject<string[]>([]);

    /**
     * Список должностей
     */
    readonly positionsList$ = new BehaviorSubject<IStringNameItem[]>([]);
    readonly positionsListLoading$ = new BehaviorSubject<boolean>(true);

    /**
     * Для списка "Руководитель" дизаблим самого себя и подчинённых
     */
    readonly userListItemService: FuiSelectItemService<IStringNameItem, string> = {
        ...defaultStringNameItemService,
        disabled: (item) => Number(item.id) === Number(this.data.id) || this.subordinates$.value.includes(item.id),
    };

    /**
     * Форма
     */
    form: FormGroup = null;

    /**
     * Ссылка на форму
     */
    @ViewChild('formRef', { static: false }) formRef: ElementRef;

    /**
     * Флаг загрузки формы
     */
    isLoaded = false;

    /*
     * Настроки валидации
     */
    validationPass = null;

    /**
     * Доступ по рубрикаторам
     */
    accessCatalogs: InterfaceAccessMatrixUnit = null;
    @ViewChild('positionRef', { static: false }) positionRef;

    /**
     * Выбранное фото
     */
    photoUrl: string = null;

    /**
     * Настройки диалога редактирования фото
     */
    cropperOptions: ICropperDialogData = {
        ...DEFAULT_CROPPER_DIALOG_DATA,
        cropOptions: {
            ...DEFAULT_CROP_OPTIONS,
            title: this.dictionary.files_crop_first_step_title,
            description: this.dictionary.files_crop_first_step_description,
            maintainAspectRatio: false,
            resizeToWidth: 600,
            resizeToHeight: 600,
        },
        avatarOptions: {
            title: this.dictionary.files_crop_two_step_title,
            description: this.dictionary.files_crop_two_step_description,
            resizeToWidth: 64,
            resizeToHeight: 64,
        },
        useAvatarStep: true,
        data: '',
    };

    /**
     * Действия для загрузки и удаления фото
     */
    readonly photoActions: IImagePickerActions = {
        upload: (data: IImagePickerData) =>
            this.service.savePhoto({
                photo: {
                    field: 'photo',
                    value: {
                        name: data.name,
                        file: data.box.split('base64,')[1],
                    },
                },
                photo_round: {
                    value: {
                        name: data.name,
                        file: data.round.split('base64,')[1],
                    },
                },
            }),
        remove: () => this.service.removePhoto({ ids: this.photoIds }),
    };

    /** Редактирование самого себя */
    private get isEditYourself() {
        return Number(this?.data?.id) === Number(this.appService.userData.id);
    }

    /** Есть доступ редактировать самого себя */
    private get canEditYourself() {
        return this.appService.accessMatrix.users.edit_yourself === 1;
    }

    /** Есть доступ редактировать всех пользователей (включая самого себя) */
    private get canEditOthers() {
        return this.appService.accessMatrix.users.edit === 1;
    }

    /** Событие окончания инициализации компонента */
    ngOnInit() {
        this.validationPass = this.appService.getSettingsByKey('password_validation');
        this.createForm(this.data.data, this.data.id);
        this.accessCatalogs = this.appService?.accessMatrix?.rubricator;
        this.disableNonProfileFields$.next(!this.canEditOthers);
        if (this.isEditYourself) {
            this.disableProfileFields$.next(!this.canEditYourself && !this.canEditOthers);
        } else {
            this.disableProfileFields$.next(!this.canEditOthers);
        }
        this.reloadPositionsList();
        this.loadSubordinates();
    }

    /**
     * Создание формы
     * @param data - данные формы
     * @param id - id пользователя
     */
    createForm(data?, id?) {
        const passwordValidators = [
            CustomValidators.patternValidator(
                /^[0-9A-Za-z\!\@\#\$\%\^\&\*\(\)\_\+\-\=\{\}\;\'\:\"\|\,\.\<\>\/\?\[\]\№\~\`\\]+$/,
                { hasLatinCharacters: true },
            ),
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
            CustomValidators.patternValidator(/[\!\@\#\$\%\^\&\*\(\)\_\+\-\=\{\}\;\'\:\"\|\,\.\<\>\/\?\[\]\№\~\`\\]/, {
                hasSpecialCharacters: true,
            }),
            Validators.minLength(8),
        ];
        this.form = this.formBuilder.group({
            firstname: [data.firstname ? data.firstname : '', [Validators.required, Validators.maxLength(255)]],
            lastname: [data.lastname ? data.lastname : '', [Validators.required, Validators.maxLength(255)]],
            middlename: [data.middlename ? data.middlename : '', [Validators.maxLength(255)]],
            roles: [data.role_id ?? [], [Validators.required]],
            subject_id: [data.subject_id ?? [], [Validators.required]],
            snils: [data.snils ? data.snils : '', []],
            login: [data.login ? data.login : '', [Validators.required, Validators.maxLength(255)]],
            passwordGroup: this.formBuilder.group(
                {
                    password: id ? ['', passwordValidators] : ['', [Validators.required, ...passwordValidators]],
                    repeat_password: id ? [] : ['', [Validators.required]],
                },
                { validator: CustomValidators.childrenEqual },
            ),
            chief: [data.chief ? data.chief : '', []],
            contacts: [data.contacts ? data.contacts : '', []],
            email: [data.email ? data.email : '', [Validators.email, Validators.maxLength(255)]],
            position: [data.position, [Validators.required]],
            company: [data.company ? data.company : '', [Validators.maxLength(255)]],
            phone: [data.phone ? data.phone : '', [Validators.maxLength(128)]],
            fax: [data.fax ? data.fax : '', [Validators.maxLength(255)]],
            address: [data.address ? data.address : '', [Validators.maxLength(255)]],
            comments: [data.comments ? data.comments : '', []],
            ip: [data.ip ? data.ip : '', [Validators.maxLength(512)]],
            active: [data.active ?? true],
            access_child: [data.access_child, []],
            date_start_deactivation: [data.date_start_deactivation ? data.date_start_deactivation : null, []],
            date_end_deactivation: [data.date_end_deactivation ? data.date_end_deactivation : null, []],
            date_birth: [data.date_birth ? data.date_birth : null, []],
            personal_email: [
                data.personal_email ? data.personal_email : null,
                [Validators.email, Validators.maxLength(255)],
            ],
            personal_phone: [data.personal_phone ? data.personal_phone : null, [Validators.maxLength(50)]],
            menu_id: [data.menu_id ?? null, []],
        });
        if (this.validationPass === '0') {
            this.form.get('passwordGroup').get('password').clearValidators();
            this.form.get('passwordGroup').get('password').setValidators(Validators.minLength(3));
        }
        this.form
            .get('active')
            .valueChanges.pipe(takeUntil(this.#destroy$))
            .subscribe(() => {
                this.form.get('date_start_deactivation').setValue(null);
                this.form.get('date_end_deactivation').setValue(null);
            });

        if (data && data.gallery && data.gallery.data) {
            this.photoIds = data.gallery.ids;
            this.photoUrl = this.serverUrl + '/public/uploads/user_photos/' + data.gallery.data?.photo?.file_name;
        }
        this.isLoaded = true;
    }

    /**
     * Закрытие формы
     */
    close() {
        this.dialogRef.close({ save: false });
    }

    /**
     * Сохранение данных
     */
    save() {
        this.formTools.validateAllFormFields(this.form);
        if (!this.form.valid) {
            this.formTools.focusFirstInvalid(this.formRef);
            return;
        }

        const data = Object.assign({}, this.form.getRawValue());
        data.gallery = this.photoIds;
        data.id = this.data.id ? this.data.id : 0;
        if (this.form.value.passwordGroup.password) {
            data.password = this.form.value.passwordGroup.password;
        }
        data.region_id = '0';
        data.chief = data.chief ? data.chief : 0;
        data.active = data.active === true ? '1' : '0';
        data.access_child = data.access_child === true ? '1' : '0';

        const useYourselfOnlyApi = this.isEditYourself && !this.canEditOthers;

        this.spinner.show();
        (useYourselfOnlyApi ? this.service.saveYourself(data) : this.service.save(data))
            .pipe(take(1), takeUntil(this.#destroy$))
            .subscribe({
                next: (response: any) => {
                    this.spinner.hide();
                    if (!response.is_error) {
                        this.dialogRef.close({ save: true, data: response.data });
                    } else {
                        AppHelper.setErrorForm(response, this.form, this.formRef, this.notifier);
                    }
                },
                error: (err: unknown) => {
                    this.spinner.hide();
                    this.notifier.exception(err);
                },
            });
    }

    /**
     * Загрузить список должностей
     */
    private reloadPositionsList() {
        this.positionsListLoading$.next(true);
        this.appService
            .getRegister('directory_of_posts')
            .pipe(
                map((items) => prepareRubricData(items, false, false, false) as IStringNameItem[]),
                takeUntil(this.#destroy$),
            )
            .subscribe({
                next: (data) => {
                    this.positionsList$.next(data);
                    this.positionsListLoading$.next(false);
                },
            });
    }

    /**
     * Загрузить список ID подчинённых
     */
    private loadSubordinates() {
        this.appService
            .getRegister(`user_subordinates(${this.data.id})`)
            .pipe(takeUntil(this.#destroy$))
            .subscribe({
                next: (data) => {
                    this.subordinates$.next(data);
                },
            });
    }

    /**
     * Добавление новой должности кнопкой из списка должностей
     */
    addNewPosition() {
        const parentRubricatorId = this.appService.getSettingsByKey('directory_of_posts');
        const dialogRef = this.dialog.open(CatalogDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '600px',
            data: {
                title: this.appService.language_dictionary.rubric_adding_data_for_rubric,
                id: 0,
                access: this.accessCatalogs,
                data: {
                    parent_id: parentRubricatorId,
                },
            },
        });
        dialogRef
            .afterClosed()
            .pipe(
                take(1),
                filter((result) => result.save),
                tap(() => this.reloadPositionsList()),
                switchMap((result) => this.positionsList$.pipe(map(() => result.data.id))),
                takeUntil(this.#destroy$),
            )
            .subscribe((newPositionId: string) => {
                const formControl = this.form.get('position');
                const currentPositions = (formControl.value ?? []) as string[];
                if (!currentPositions.includes(newPositionId)) {
                    currentPositions.push(newPositionId);
                }
                this.form.get('position').setValue(currentPositions);
            });
    }

    /**
     * ЕСИА
     */
    authByEsia() {
        document.location.href = this.appService.serverUrl + '/esia/connect';
    }

    /**
     * Обработка загрузки файлов
     */
    fileUploaded(data: any) {
        this.photoIds = data.ids;
    }

    /**
     * Обработка ошибок загрузки и удаления файлов
     */
    fileFailed(err: unknown) {
        this.notifier.exception(err);
    }

    /**
     * Обработка удаления файлов
     */
    fileRemoved() {
        this.photoUrl = null;
        this.photoIds = [];
    }
}
